import * as React from "react";
import { useList } from "@refinedev/core";
import { SkeletonTable } from "@/components/SkeletonTable";
import { EmptyState } from "@/components/EmptyState";
import { ErrorBanner } from "@/components/ErrorBanner";
import { CursorPagination } from "@/components/CursorPagination";
import { RoleBadge } from "@/components/RoleBadge";
import type { AdminRole } from "@/providers/types";
import {
  buildAuditRefineFilters,
  EMPTY_AUDIT_FILTERS,
} from "@/lib/auditFilters";
import type { AuditFiltersState } from "@/lib/auditFilters";

export type AuditEntry = {
  id: string;
  actorId: string;
  actorEmail: string;
  actorRole: AdminRole;
  action: string;
  targetAccount: string | null;
  accessedFields: string[];
  reason: string | null;
  timestamp: string;
};

export function formatAuditDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "UTC",
    })
      .format(new Date(iso))
      .replace(",", "");
  } catch {
    return iso;
  }
}

interface AuditEntryRowProps {
  entry: AuditEntry;
}

export function AuditEntryRow({ entry }: AuditEntryRowProps): React.JSX.Element {
  return (
    <tr className="border-b border-[var(--border)] last:border-b-0 min-h-[44px] transition-colors hover:bg-[var(--accent-weak)]">
      <td className="px-3.5 py-2.5 whitespace-nowrap text-[var(--muted)] text-[12px] font-mono w-[150px]">
        {formatAuditDate(entry.timestamp)}
      </td>
      <td className="px-3.5 py-2.5 whitespace-nowrap w-[220px]">
        <span className="font-[500] text-[13px]">{entry.actorEmail}</span>
        <RoleBadge role={entry.actorRole} />
      </td>
      <td className="px-3.5 py-2.5 font-mono text-[12px] text-[var(--fg)] whitespace-nowrap w-[180px]">
        {entry.action}
      </td>
      <td className="px-3.5 py-2.5 text-[13px] w-[200px]">
        {entry.targetAccount ?? "—"}
      </td>
      <td className="px-3.5 py-2.5 text-[13px] text-[var(--muted)] max-w-[200px]">
        {entry.accessedFields.length > 0 ? (
          <span className="line-clamp-1">{entry.accessedFields.join(", ")}</span>
        ) : "—"}
      </td>
      <td className="px-3.5 py-2.5 text-[13px] text-[var(--muted)] max-w-[240px]">
        <span className="line-clamp-1">{entry.reason ?? "—"}</span>
      </td>
    </tr>
  );
}

interface AuditFiltersBarProps {
  value: AuditFiltersState;
  onChange: (f: AuditFiltersState) => void;
  onApply: () => void;
  onReset: () => void;
}

export function AuditFiltersBar({
  value,
  onChange,
  onApply,
  onReset,
}: AuditFiltersBarProps): React.JSX.Element {
  return (
    <div
      role="search"
      aria-label="Фильтры журнала аудита"
      className="sticky top-0 z-[5] bg-[var(--surface)] border-b border-[var(--border)] px-6 py-3 flex items-center gap-3 flex-wrap"
    >
      <div>
        <label htmlFor="filter-actor" className="sr-only">
          Оператор
        </label>
        <input
          id="filter-actor"
          type="text"
          placeholder="Оператор (actor ID)…"
          value={value.actor_id}
          onChange={(e) => onChange({ ...value, actor_id: e.target.value })}
          className="h-9 px-3 border border-[var(--border)] rounded-[var(--radius-sm)] text-[13px] font-[var(--font)] text-[var(--fg)] bg-[var(--surface-2)] outline-none focus:border-[var(--accent)] w-48"
        />
      </div>

      <div>
        <label htmlFor="filter-action" className="sr-only">
          Действие
        </label>
        <input
          id="filter-action"
          type="text"
          placeholder="Действие…"
          value={value.action}
          onChange={(e) => onChange({ ...value, action: e.target.value })}
          className="h-9 px-3 border border-[var(--border)] rounded-[var(--radius-sm)] text-[13px] font-[var(--font)] text-[var(--fg)] bg-[var(--surface-2)] outline-none focus:border-[var(--accent)] w-40"
        />
      </div>

      <div className="flex items-center gap-1.5">
        <label htmlFor="filter-from" className="text-[13px] text-[var(--muted)] whitespace-nowrap">
          С
        </label>
        <input
          id="filter-from"
          type="date"
          value={value.dateFrom}
          onChange={(e) => onChange({ ...value, dateFrom: e.target.value })}
          className="h-9 px-3 border border-[var(--border)] rounded-[var(--radius-sm)] text-[13px] font-[var(--font)] text-[var(--fg)] bg-[var(--surface-2)] outline-none focus:border-[var(--accent)]"
        />
      </div>

      <div className="flex items-center gap-1.5">
        <label htmlFor="filter-to" className="text-[13px] text-[var(--muted)] whitespace-nowrap">
          По
        </label>
        <input
          id="filter-to"
          type="date"
          value={value.dateTo}
          onChange={(e) => onChange({ ...value, dateTo: e.target.value })}
          className="h-9 px-3 border border-[var(--border)] rounded-[var(--radius-sm)] text-[13px] font-[var(--font)] text-[var(--fg)] bg-[var(--surface-2)] outline-none focus:border-[var(--accent)]"
        />
      </div>

      <button
        type="button"
        onClick={onApply}
        className="min-h-[36px] px-4 py-1.5 bg-[var(--accent)] text-white text-[13px] font-[600] rounded-[var(--radius-sm)] hover:opacity-90 transition-opacity focus-visible:outline-2 focus-visible:outline-[var(--accent)] focus-visible:outline-offset-2"
      >
        Применить
      </button>

      <button
        type="button"
        onClick={onReset}
        className="min-h-[36px] px-4 py-1.5 border border-[var(--border)] text-[var(--muted)] text-[13px] font-[600] rounded-[var(--radius-sm)] hover:bg-[var(--surface-2)] transition-colors focus-visible:outline-2 focus-visible:outline-[var(--accent)] focus-visible:outline-offset-2"
      >
        Сбросить
      </button>
    </div>
  );
}

export function AuditList(): React.JSX.Element {
  const [filters, setFilters] = React.useState<AuditFiltersState>(EMPTY_AUDIT_FILTERS);
  const [appliedFilters, setAppliedFilters] = React.useState<AuditFiltersState>(EMPTY_AUDIT_FILTERS);
  const [cursor, setCursor] = React.useState<string | undefined>(undefined);
  const [entries, setEntries] = React.useState<AuditEntry[]>([]);

  const activeFilters = buildAuditRefineFilters(appliedFilters);

  const { query } = useList<AuditEntry>({
    resource: "audit",
    filters: activeFilters,
    meta: { cursor },
  });

  const data = query.data;
  const isLoading = query.isLoading;
  const isError = query.isError;
  const refetch = query.refetch;

  React.useEffect(() => {
    if (data?.data) {
      const incoming = data.data as AuditEntry[];
      if (!cursor) {
        setEntries(incoming);
      } else {
        setEntries((prev) => {
          const seen = new Set(prev.map((e) => e.id));
          return [...prev, ...incoming.filter((e) => !seen.has(e.id))];
        });
      }
    }
  }, [data?.data, cursor]);

  const nextCursor =
    (data as { meta?: { nextCursor?: string | null } } | undefined)?.meta?.nextCursor ?? null;

  const handleApply = () => {
    setCursor(undefined);
    setEntries([]);
    setAppliedFilters({ ...filters });
  };

  const handleReset = () => {
    setFilters(EMPTY_AUDIT_FILTERS);
    setAppliedFilters(EMPTY_AUDIT_FILTERS);
    setCursor(undefined);
    setEntries([]);
  };

  const showEmpty = !isLoading && !isError && entries.length === 0;
  const showTable = !isLoading && entries.length > 0;

  return (
    <div className="flex flex-col min-h-screen">
      <div className="px-6 pt-5 pb-0">
        <h1 className="text-[22px] font-[700] tracking-[-0.3px]">Журнал аудита</h1>
      </div>

      <AuditFiltersBar
        value={filters}
        onChange={setFilters}
        onApply={handleApply}
        onReset={handleReset}
      />

      <div className="px-6 pb-6 flex-1">
        {isLoading && (
          <div className="mt-3 bg-[var(--surface)] rounded-[var(--radius)] border border-[var(--border)] overflow-hidden">
            <SkeletonTable columns={6} rows={6} />
          </div>
        )}

        {isError && (
          <div className="mt-3">
            <ErrorBanner
              message="Не удалось загрузить журнал аудита. Проверьте подключение к сети и попробуйте снова."
              onRetry={() => void refetch()}
            />
          </div>
        )}

        {showEmpty && <EmptyState message="Нет записей по заданному фильтру" />}

        {showTable && (
          <table className="w-full border-collapse mt-3 bg-[var(--surface)] rounded-[var(--radius)] overflow-hidden border border-[var(--border)]">
            <thead className="bg-[var(--surface-2)]">
              <tr>
                <th
                  scope="col"
                  className="w-[150px] px-3.5 py-2.5 text-[12px] font-[600] text-[var(--muted)] uppercase tracking-[0.5px] text-left border-b border-[var(--border)] whitespace-nowrap"
                >
                  Дата/время
                </th>
                <th
                  scope="col"
                  className="w-[220px] px-3.5 py-2.5 text-[12px] font-[600] text-[var(--muted)] uppercase tracking-[0.5px] text-left border-b border-[var(--border)] whitespace-nowrap"
                >
                  Оператор
                </th>
                <th
                  scope="col"
                  className="w-[180px] px-3.5 py-2.5 text-[12px] font-[600] text-[var(--muted)] uppercase tracking-[0.5px] text-left border-b border-[var(--border)] whitespace-nowrap"
                >
                  Действие
                </th>
                <th
                  scope="col"
                  className="w-[200px] px-3.5 py-2.5 text-[12px] font-[600] text-[var(--muted)] uppercase tracking-[0.5px] text-left border-b border-[var(--border)] whitespace-nowrap"
                >
                  Объект
                </th>
                <th
                  scope="col"
                  className="w-[200px] px-3.5 py-2.5 text-[12px] font-[600] text-[var(--muted)] uppercase tracking-[0.5px] text-left border-b border-[var(--border)] whitespace-nowrap"
                >
                  Поля
                </th>
                <th
                  scope="col"
                  className="px-3.5 py-2.5 text-[12px] font-[600] text-[var(--muted)] uppercase tracking-[0.5px] text-left border-b border-[var(--border)] whitespace-nowrap"
                >
                  Причина
                </th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <AuditEntryRow key={entry.id} entry={entry} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      <CursorPagination
        nextCursor={nextCursor}
        onLoadMore={() => setCursor(nextCursor ?? undefined)}
        isLoading={isLoading}
      />
    </div>
  );
}
